import { PaceBarcode } from '@evorait/pace-components';
import { LoadStatus } from '../load-status';

/**
 * Feature key under which the barcode clipboard slice is registered in the root store.
 */
export const BARCODE_CLIPBOARD_FEATURE_KEY = 'barcodeClipboard';

/**
 * Retention policy for the clipboard: only the most recent `BARCODE_CLIPBOARD_MAX_QUEUE` barcodes
 * are kept. Lives with the state because the cap is reducer policy, applied by
 * {@link capBarcodeQueue}.
 */
export const BARCODE_CLIPBOARD_MAX_QUEUE = 10;

// Re-export so the slice files (actions/reducer/selectors) can keep importing the plain
// barcode shape from the state barrel. The canonical definition lives with the component
// library (`PaceBarcode` is already a plain, serializable DTO - no converter needed).
export type { PaceBarcode };

/**
 * The barcode clipboard slice: a capped, most-recent-first list of barcodes offered as
 * quick-pick candidates from the scanner modal. `status` tracks whether the clipboard has been
 * resolved from storage yet (see {@link LoadStatus}).
 */
export type BarcodeClipboardState = Readonly<{
    barcodes: readonly PaceBarcode[];
    status: LoadStatus;
}>;

export const initialBarcodeClipboardState: BarcodeClipboardState = {
    barcodes: [],
    status: LoadStatus.Idle,
};

/**
 * Coerces a missing/oversized queue to the retention policy: `undefined` becomes an empty
 * clipboard, and anything longer than {@link BARCODE_CLIPBOARD_MAX_QUEUE} is truncated to its
 * most recent entries. Used by the reducer only - callers never need to cap before dispatching.
 *
 * @param barcodes the candidate barcode collection
 * @returns the collection coerced to the retention policy
 */
export function capBarcodeQueue(barcodes?: readonly PaceBarcode[]): readonly PaceBarcode[] {
    if (!barcodes?.length) {
        return [];
    }
    return barcodes.length > BARCODE_CLIPBOARD_MAX_QUEUE ? barcodes.slice(0, BARCODE_CLIPBOARD_MAX_QUEUE) : barcodes;
}
